import { memo, useContext, useEffect, useMemo, useState } from "react";
import { Autocomplete, Button, Chip, TextField, Paper, Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import {
  Search as SearchIcon,
  FilterAltOff as ClearIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";

import {
  alarmFiltersSelector,
  clearTemporaryFilters,
  DEFAULT_ALARM_FILTERS,
  setAlarmFilters,
  setTemporaryAlarmFilters,
  temporaryAlarmFiltersSelector,
  updateAlarmFilters,
  updateTemporaryAlarmFilters,
} from "./alarmsSlice";
import {
  useGetAlarmTypesQuery,
  useGetDevicesNameQuery,
  useGetDeviceTypesQuery,
} from "../SearchFilters/searchFiltersSlice";
import { extractUserDataFromPermission, getAlarmCategoryList } from "../../Utils/Utils";
import { ActiveAlarmsData } from "../../Components/Alarms/ActiveAlarms";
import { getDeviceNameSearchParams, getDeviceTypeSearchParams } from "../SearchFilters/searchFiltersUtils";
import { AutocompleteStationListPop } from "../../Components/AutocompleteStationListPop/AutocompleteStationListPop";
import { selectedCustomersSelector } from "../authentication/auth-slice";
import { getParsedLocalStorage } from "../../Utils/storage-utils";
import { getAlarmSeverityLabel } from "./alarmUtils";

const operation = (list1, list2, isUnion = false) =>
  list1.filter(
    (
      (set) => (a) =>
        isUnion === set.has(a.id)
    )(new Set(list2.map((b) => b.id))),
  );

// Following functions are to be used:
const inBoth = (list1, list2) => {
  if (!list1 || !list2 || list1?.length === 0 || list2?.length === 0) return [];
  return operation(list1, list2, true);
};

const AlarmsFilters = () => {
  const temporaryFilters = useSelector(temporaryAlarmFiltersSelector);
  const alarmFilters = useSelector(alarmFiltersSelector);
  const dispatch = useDispatch();
  const system_options = useMemo(() => {
    const systems = getParsedLocalStorage("systems_list", []);
    return systems.map((system) => ({
      systemId: system.id,
      systemName: system.code,
    }));
  }, []);

  // Extract Zone/Division/Station hierarchy from permissions
  const permissions = useMemo(() => JSON.parse(localStorage.getItem("permissions") || "{}"), []);
  const {
    outermostKeys: zones,
    outerToInnerKeys: zoneToDivision,
    innerToCustomerNames: divisionToStation,
    customerNameToId: customerNameToId,
  } = useMemo(() => extractUserDataFromPermission(permissions), [permissions]);

  // Helper functions for Zone/Division/Station cascading
  const getAllDivisionsForZones = (zoneList) => {
    let divList = [];
    (zoneList ?? []).forEach((zone) => {
      (zoneToDivision?.[zone?.id] ?? []).forEach((divCode) => {
        divList.push({ id: divCode, value: divCode });
      });
    });
    return divList;
  };

  const getAllStationsForDivisions = (divList) => {
    let stnList = [];
    (divList ?? []).forEach((div) => {
      (divisionToStation?.[div?.id] ?? []).forEach((stnName) => {
        stnList.push({ id: customerNameToId?.[stnName]?.[0], value: stnName });
      });
    });
    return stnList;
  };

  const INITIAL_FILTERS_STATE = temporaryFilters ?? alarmFilters;
  // const INITIAL_FILTERS_STATE = useMemo(() => {
  //   return temporaryFilters ?? alarmFilters;
  // }, [temporaryFilters, alarmFilters]);

  const [filterState, setFilterState] = useState(INITIAL_FILTERS_STATE);

  // Zone dropdown options
  const zoneOptions = useMemo(() => {
    if (!zones || zones.length === 0) return [];
    const options = zones.map((zoneCode) => ({ id: zoneCode, value: zoneCode }));
    options.sort((a, b) => a.value.localeCompare(b.value));
    return options;
  }, [zones]);

  // Division dropdown options - filtered by selected zones
  const divisionOptions = useMemo(() => {
    const selectedZones = filterState?.zone_codes ?? [];
    if (selectedZones.length === 0) {
      // If no zones selected, show all divisions
      let allDivisions = [];
      Object.values(zoneToDivision || {}).forEach((divs) => {
        divs.forEach((divCode) => {
          if (!allDivisions.find((d) => d.id === divCode)) {
            allDivisions.push({ id: divCode, value: divCode });
          }
        });
      });
      allDivisions.sort((a, b) => a.value.localeCompare(b.value));
      return allDivisions;
    }
    const divList = getAllDivisionsForZones(selectedZones);
    divList.sort((a, b) => a.value.localeCompare(b.value));
    return divList;
  }, [filterState?.zone_codes, zoneToDivision]);

  // Station dropdown options - filtered by selected divisions (and zones)
  const stationOptions = useMemo(() => {
    const selectedZones = filterState?.zone_codes ?? [];
    const selectedDivisions = filterState?.division_codes ?? [];

    // If divisions are selected, filter by divisions
    if (selectedDivisions.length > 0) {
      const stnList = getAllStationsForDivisions(selectedDivisions);
      stnList.sort((a, b) => a.value.localeCompare(b.value));
      return stnList;
    }

    // If only zones are selected, show all stations in those zones
    if (selectedZones.length > 0) {
      const allDivisions = getAllDivisionsForZones(selectedZones);
      const stnList = getAllStationsForDivisions(allDivisions);
      stnList.sort((a, b) => a.value.localeCompare(b.value));
      return stnList;
    }

    // If nothing selected, show all permitted stations
    let allStations = [];
    Object.entries(divisionToStation || {}).forEach(([_, stns]) => {
      stns.forEach((stnName) => {
        const stnId = customerNameToId?.[stnName]?.[0];
        if (stnId && !allStations.find((s) => s.id === stnId)) {
          allStations.push({ id: stnId, value: stnName });
        }
      });
    });
    allStations.sort((a, b) => a.value.localeCompare(b.value));
    return allStations;
  }, [filterState?.zone_codes, filterState?.division_codes, divisionToStation, customerNameToId]);

  // When zones change, filter out invalid divisions and stations
  useEffect(() => {
    const selectedZones = filterState?.zone_codes ?? [];
    if (selectedZones.length === 0) return;

    const validDivisions = getAllDivisionsForZones(selectedZones);
    const currentDivisions = filterState?.division_codes ?? [];
    const filteredDivisions = inBoth(validDivisions, currentDivisions);

    const validStations = getAllStationsForDivisions(filteredDivisions.length > 0 ? filteredDivisions : validDivisions);
    const currentStations = filterState?.customer_ids ?? [];
    const filteredStations = inBoth(validStations, currentStations);

    setFilterState((state) => ({
      ...state,
      division_codes: filteredDivisions,
      customer_ids: filteredStations,
    }));
  }, [filterState?.zone_codes]);

  // When divisions change, filter out invalid stations
  useEffect(() => {
    const selectedDivisions = filterState?.division_codes ?? [];
    if (selectedDivisions.length === 0) return;

    const validStations = getAllStationsForDivisions(selectedDivisions);
    const currentStations = filterState?.customer_ids ?? [];
    const filteredStations = inBoth(validStations, currentStations);

    setFilterState((state) => ({
      ...state,
      customer_ids: filteredStations,
    }));
  }, [filterState?.division_codes]);

  const { selectedCustomersIdName = [], customerIdList = [] } = useContext(ActiveAlarmsData);
  const selectedCustomersList = useSelector(selectedCustomersSelector);
  const categoriesList = getAlarmCategoryList();
  const { data: gearTypesList = [] } = useGetDeviceTypesQuery(
    getDeviceTypeSearchParams({ filterState, customerIdList }),
  );
  const { data: deviceNamesList = [] } = useGetDevicesNameQuery(
    getDeviceNameSearchParams({ filterState, customerIdList }),
  );
  const { data: alarmTypesList = [] } = useGetAlarmTypesQuery(null);

  useEffect(() => {
    const customer_ids = filterState.customer_ids?.filter((customer) => customerIdList.includes(customer.id));
    if (temporaryFilters) {
      dispatch(updateTemporaryAlarmFilters({ ...filterState, customer_ids }));
      return;
    }
    dispatch(updateAlarmFilters({ ...filterState, customer_ids }));
  }, [customerIdList.length]);

  useEffect(() => {
    if (!gearTypesList?.length) return;
    const device_type_ids = inBoth(gearTypesList, filterState.device_type_ids);
    setFilterState((state) => ({ ...state, device_type_ids }));
  }, [gearTypesList]);

  useEffect(() => {
    if (!deviceNamesList?.length) return;
    const device_ids = inBoth(deviceNamesList, filterState.deviceNamesList);
    console.log("dlksghdjkfhg", deviceNamesList, filterState.deviceNamesList);
    setFilterState((state) => ({ ...state, device_ids }));
  }, [deviceNamesList]);

  const permittedCustomerIds = useMemo(() => {
    const _list = selectedCustomersList;
    if (!_list) return [];
    return _list.map((customer) => ({ id: customer.customerId, value: customer.customerName }));
  }, [selectedCustomersList]);

  const handleSearch = () => {
    console.log("temporaryFilters", temporaryFilters, filterState);
    if (temporaryFilters) {
      dispatch(setTemporaryAlarmFilters(filterState));
      return;
    }
    dispatch(updateAlarmFilters(filterState));
  };

  const handleClear = () => {
    if (temporaryFilters) {
      dispatch(clearTemporaryFilters());
      return;
    }
    const _filters = { ...DEFAULT_ALARM_FILTERS, is_active: alarmFilters.is_active };
    dispatch(setAlarmFilters(_filters));
  };

  useEffect(() => {
    setFilterState(INITIAL_FILTERS_STATE);
  }, [temporaryFilters, alarmFilters]);

  return (
    <Paper
      elevation={0}
      className="alarm-filters-container"
      sx={{
        backgroundColor: 'var(--color-white)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-color)',
        padding: '1rem',
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        marginBottom: '0.75rem',
        paddingBottom: '0.75rem',
        borderBottom: '1px solid var(--border-color)',
      }}>
        <FilterIcon sx={{ color: 'var(--primary-600)', fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{
          fontWeight: 600,
          color: 'var(--neutral-700)',
          fontSize: '0.875rem',
        }}>
          Filter Alarms
        </Typography>
      </Box>
      <Box className="alarm-filters-grid">
      {/* Zone Filter */}
      <Autocomplete
        size="small"
        className="filter-input auto-complete"
        multiple
        disableCloseOnSelect
        options={zoneOptions}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField className="filter-input" size="small" {...params} label="Zone" placeholder="Zone" />
        )}
        getOptionLabel={(zone) => zone.value}
        value={filterState?.zone_codes ?? []}
        onChange={(_, newValue) => {
          setFilterState((state) => ({
            ...state,
            zone_codes: newValue,
          }));
        }}
        renderTags={(value, getTagProps) => {
          return (
            <>
              <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps(0)} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((zone) => zone?.value ?? "").toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} />
                </AutocompleteStationListPop>
              )}
            </>
          );
        }}
      />
      {/* Division Filter */}
      <Autocomplete
        size="small"
        className="filter-input auto-complete"
        multiple
        disableCloseOnSelect
        options={divisionOptions}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField className="filter-input" size="small" {...params} label="Division" placeholder="Division" />
        )}
        getOptionLabel={(division) => division.value}
        value={filterState?.division_codes ?? []}
        onChange={(_, newValue) => {
          setFilterState((state) => ({
            ...state,
            division_codes: newValue,
          }));
        }}
        renderTags={(value, getTagProps) => {
          return (
            <>
              <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps(0)} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((div) => div?.value ?? "").toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} />
                </AutocompleteStationListPop>
              )}
            </>
          );
        }}
      />
      {/* Station Filter */}
      <Autocomplete
        size="small"
        className="filter-input auto-complete"
        multiple
        disableCloseOnSelect
        options={stationOptions}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField className="filter-input" size="small" {...params} label="Stations" placeholder="Stations" />
        )}
        getOptionLabel={(customer) => customer.value}
        value={filterState?.customer_ids ?? []}
        onChange={(_, newValue) => {
          setFilterState((state) => ({
            ...state,
            customer_ids: newValue,
          }));
        }}
        renderTags={(value, getTagProps) => {
          return (
            <>
              <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps(0)} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((stn) => stn?.value ?? "").toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} />
                </AutocompleteStationListPop>
              )}
            </>
          );
        }}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          disableFuture
          label="From Date"
          openTo="day"
          views={["year", "month", "day"]}
          value={filterState.from_ts}
          onChange={(newValue) => setFilterState((prev) => ({ ...prev, from_ts: newValue }))}
          renderInput={(params) => <TextField size="small" {...params} />}
        />
        <DatePicker
          disableFuture
          label="To Date"
          openTo="day"
          views={["year", "month", "day"]}
          value={filterState.to_ts}
          onChange={(newValue) => setFilterState((prev) => ({ ...prev, to_ts: newValue }))}
          renderInput={(params) => <TextField size="small" {...params} />}
        />
      </LocalizationProvider>
      <Autocomplete
        size="small"
        multiple
        limitTags={2}
        disableCloseOnSelect
        options={categoriesList}
        value={filterState.categories ?? []}
        onChange={(_, value) => setFilterState((prev) => ({ ...prev, categories: value }))}
        renderTags={(value, getTagProps) => {
          return (
            <>
              <Chip size="small" variant="outlined" label={value[0]} {...getTagProps(0)} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((cat) => cat).toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} />
                </AutocompleteStationListPop>
              )}
            </>
          );
        }}
        renderInput={(params) => <TextField size="small" {...params} label="Categories" placeholder="Categories" />}
      />
      <Autocomplete
        size="small"
        multiple
        disableCloseOnSelect
        limitTags={2}
        options={gearTypesList}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={filterState.device_type_ids ?? []}
        getOptionLabel={(gearType) => gearType.value}
        onChange={(_, value) =>
          setFilterState((prev) => ({
            ...prev,
            device_type_ids: value,
          }))
        }
        renderTags={(value, getTagProps) => {
          return (
            <>
              <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps(0)} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((type) => type.value).toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} sx={{ flexShrink: 0 }} />
                </AutocompleteStationListPop>
              )}
            </>
          );
        }}
        renderInput={(params) => <TextField size="small" {...params} label="Asset Types" placeholder="Asset Types" />}
      />

      <Autocomplete
        size="small"
        limitTags={2}
        value={filterState.severity}
        options={["WARNING", "CRITICAL", "MAINTENANCE"]}
        onChange={(_, value) => setFilterState((prev) => ({ ...prev, severity: value }))}
        renderInput={(params) => <TextField size="small" {...params} label="Severity" placeholder="Severity" />}
        getOptionLabel={getAlarmSeverityLabel}
        // renderTags={(value, getTagProps) => {
        //   return (
        //     <div>
        //       <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps(0)} />
        //       {value.length > 1 && <Chip size="small" label={`+${value.length - 1}`} />}
        //     </div>
        //   );
        // }}
      />
      <Autocomplete
        size="small"
        multiple
        disableCloseOnSelect
        limitTags={2}
        value={filterState.device_ids ?? []}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        options={deviceNamesList}
        getOptionLabel={(deviceNames) => deviceNames.value}
        onChange={(_, value) =>
          setFilterState((prev) => ({
            ...prev,
            device_ids: value,
          }))
        }
        renderTags={(value, getTagProps) => {
          return (
            <>
              <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps(0)} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((device) => device.value).toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} sx={{ flexShrink: 0 }} />
                </AutocompleteStationListPop>
              )}
            </>
          );
        }}
        renderInput={(params) => <TextField size="small" {...params} label="Asset Names" placeholder="Asset Names" />}
      />

      <Autocomplete
        size="small"
        multiple
        disableCloseOnSelect
        limitTags={2}
        value={filterState.alarm_types ?? []}
        options={alarmTypesList}
        onChange={(_, value) =>
          setFilterState((prev) => ({
            ...prev,
            alarm_types: value,
          }))
        }
        renderTags={(value, getTagProps) => {
          return (
            <>
              <Chip size="small" variant="outlined" label={value[0]} {...getTagProps(0)} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((type) => type).toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} sx={{ flexShrink: 0 }} />
                </AutocompleteStationListPop>
              )}
            </>
          );
        }}
        renderInput={(params) => <TextField size="small" {...params} label="Alarm Types" placeholder="Alarm Types" />}
      />

      <Autocomplete
        size="small"
        multiple
        disableCloseOnSelect
        limitTags={2}
        isOptionEqualToValue={(option, value) => option.systemId === value.systemId}
        getOptionKey={(option) => option.systemId}
        value={filterState.systems ?? []}
        options={system_options}
        getOptionLabel={(option) => option.systemName}
        onChange={(_, value) =>
          setFilterState((prev) => ({
            ...prev,
            systems: value,
          }))
        }
        renderTags={(value, getTagProps) => {
          return (
            <>
              <Chip size="small" variant="outlined" label={value[0].systemName} {...getTagProps(0)} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((sys) => sys.systemName).toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} />
                </AutocompleteStationListPop>
              )}
            </>
          );
        }}
        renderInput={(params) => <TextField size="small" {...params} label="System" placeholder="System" />}
      />

      <TextField
        label="Remarks"
        size="small"
        onChange={(event) =>
          setFilterState((prev) => ({
            ...prev,
            remarks: event.target.value,
          }))
        }
        value={filterState.remarks ?? ""}
        placeholder="Remarks"
      />

      <Autocomplete
        size="small"
        disableCloseOnSelect
        isOptionEqualToValue={(option, value) => option.id === value.id}
        options={[
          { value: "Acked", id: true },
          { value: "Not Acked", id: false },
        ]}
        getOptionLabel={(status) => status.value}
        value={filterState.is_acked ?? null}
        onChange={(_, value) => setFilterState((prev) => ({ ...prev, is_acked: value }))}
        renderInput={(params) => <TextField size="small" {...params} label="Ack Status" placeholder="Ack Status" />}
      />

      </Box>
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
        marginTop: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--border-color)',
      }}>
        <Button
          variant="outlined"
          onClick={handleClear}
          startIcon={<ClearIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderColor: 'var(--neutral-300)',
            color: 'var(--neutral-600)',
            '&:hover': {
              backgroundColor: 'var(--neutral-50)',
              borderColor: 'var(--neutral-400)',
            },
          }}
        >
          Clear Filters
        </Button>
        <Button
          variant="contained"
          onClick={handleSearch}
          startIcon={<SearchIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%)',
            boxShadow: '0 2px 4px rgba(10, 93, 194, 0.2)',
            '&:hover': {
              background: 'linear-gradient(135deg, var(--primary-500) 0%, var(--primary-600) 100%)',
            },
          }}
        >
          Search
        </Button>
      </Box>
    </Paper>
  );
};

export default memo(AlarmsFilters);
